import React, { useState, useEffect } from 'react'
import Input from 'aldoo-ra/Input'
import Typography from 'aldoo-ra/Typography'
import Button from 'aldoo-ra/Button'
import Tooltip from 'aldoo-ra/Tooltip'
import { XMarkIcon, LanguageIcon, PhotoIcon, TrashIcon } from '@heroicons/react/24/outline'
import LocalizationItemEditor from 'aldoo-ra/Localization/localizaiton-item-editor'
import generateLocalizationKey from 'aldoo-ra/CMS/generate-localization-key'
import MediaLibrary from 'aldoo-ra/MediaLibrary'
import { url } from 'aldoo-ra/CMS/url'

// Define components outside to prevent recreation on each render
const LocalizationButton = ({ fieldName, pageId, editor, openLocalization }) => {
  if (!pageId) return null

  const isLocalized = editor.page?.localization?.find(
    (item) => item.field === fieldName
  )

  return (
    <Tooltip content={isLocalized ? "Edit Localization" : "Add Localization"} position="top">
      <button
        onClick={() => openLocalization(fieldName)}
        className={`w-8 h-8 rounded-full border flex items-center justify-center transition-colors ${
          isLocalized
            ? 'border-admin_primary text-admin_primary hover:bg-admin_primary hover:text-white'
            : 'border-admin_bordered text-admin_text_secondary hover:text-admin_primary hover:border-admin_primary'
        }`}
        type="button"
      >
        <LanguageIcon className="w-5 h-5" />
      </button>
    </Tooltip>
  )
}

const FieldRow = ({ label, fieldName, children, hint, pageId, editor, openLocalization }) => {
  // Check localization status directly from editor.page
  const isLocalized = React.useMemo(() => {
    return !!editor.page?.localization?.find(
      (item) => item.field === fieldName
    )
  }, [editor.page?.localization, fieldName])

  return (
    <div className="grid grid-cols-[200px_1fr] gap-4 items-start py-3 border-b border-admin_bordered last:border-0">
      <div className="pt-2">
        <div className="flex items-center gap-2">
          <Typography variant="admin_label">{label}</Typography>
          {isLocalized && (
            <span className="px-2 py-0.5 text-xs rounded bg-admin_primary/10 text-admin_primary border border-admin_primary/20">
              Localized
            </span>
          )}
        </div>
        {hint && (
          <Typography variant="admin_txt1_dim" className="text-xs mt-1">
            {hint}
          </Typography>
        )}
      </div>
      <div className="pt-1 flex gap-2 items-start">
        <div className="flex-1">
          {React.cloneElement(children, {
            disabled: isLocalized,
            readOnly: isLocalized,
            className: children.props.className
          })}
        </div>
        <LocalizationButton
          fieldName={fieldName}
          pageId={pageId}
          editor={editor}
          openLocalization={openLocalization}
        />
      </div>
    </div>
  )
}

const SEOPlugin = ({ editor, onClose }) => {
  const [seoData, setSeoData] = useState({
    title: '',
    description: '',
    keywords: '',
    ogImage: '',
  })
  const [localizationEditor, setLocalizationEditor] = useState({
    isOpen: false,
    field: null,
    key: null
  })
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false)

  // Get page ID from editor API
  const pageId = editor.pageId

  useEffect(() => {
    // Load existing SEO data from page content only on mount
    if (editor.content) {
      setSeoData({
        title: editor.content.seo_title || editor.content.name || '',
        description: editor.content.seo_description || '',
        keywords: editor.content.seo_keywords || '',
        ogImage: editor.content.seo_og_image || '',
      })
    }
  }, [pageId]) // Only re-run when page ID changes (different page loaded)

  const handleChange = (field, value) => {
    const newSeoData = { ...seoData, [field]: value }
    setSeoData(newSeoData)

    // Update page content directly using the updateContent API
    const fieldMap = {
      'title': 'seo_title',
      'description': 'seo_description',
      'keywords': 'seo_keywords',
      'ogImage': 'seo_og_image',
    }

    editor.updateContent({
      [fieldMap[field]]: value
    })
  }

  const getLocalizationKey = (fieldName) => {
    if (!pageId) return null
    return generateLocalizationKey({
      type: 'AldooPage',
      fieldID: fieldName,
      instanceID: pageId,
    })
  }

  const openLocalization = async (fieldName) => {
    const key = getLocalizationKey(fieldName)
    if (!key) {
      console.warn('Cannot open localization: page not saved yet')
      return
    }

    // Check if this field is already localized
    const isLocalized = editor.page?.localization?.find(
      (item) => item.field === fieldName
    )

    // If not localized, add it to the localization array directly
    if (!isLocalized) {
      if (!editor.page.localization) {
        editor.page.localization = []
      }
      editor.page.localization.push({
        field: fieldName,
        localizationKey: key,
      })

      // Create initial localization entry with current field value
      try {
        const { SaveLocalizationItem } = await import('aldoo-ra/Localization/api')
        const fieldMap = {
          'seo_title': seoData.title,
          'seo_description': seoData.description,
          'seo_keywords': seoData.keywords,
          'seo_og_image': seoData.ogImage
        }
        const currentValue = fieldMap[fieldName] || ''

        // Determine the type based on field name
        const fieldType = fieldName === 'seo_og_image' ? 'image' : 'text'

        await SaveLocalizationItem({
          key,
          language: 'en',
          content: currentValue,
          type: fieldType
        })
      } catch (error) {
        console.error('Failed to create initial localization entry:', error)
      }
    }

    setLocalizationEditor({
      isOpen: true,
      field: fieldName,
      key
    })
  }

  const handleFileSelect = (file) => {
    if (!file) return

    // Get the file path from either file.path or file.url
    const filePath = file.path || file.url

    if (filePath) {
      handleChange('ogImage', filePath)
    }

    setIsMediaLibraryOpen(false)
  }

  const handleRemoveImage = (e) => {
    e.stopPropagation()
    handleChange('ogImage', '')
  }

  return (
    <>
      <div className="flex flex-col h-full">
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-1.5 rounded hover:bg-admin_neutral_1/50 text-admin_text_secondary hover:text-admin_error transition-colors z-10"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          <div className="max-w-3xl mx-auto">
            <FieldRow
              label="Page Title"
              fieldName="seo_title"
              hint="50-60 characters"
              pageId={pageId}
              editor={editor}
              openLocalization={openLocalization}
            >
              <Input
                variant="admin"
                value={seoData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Enter SEO title..."
              />
            </FieldRow>

            <FieldRow
              label="Meta Description"
              fieldName="seo_description"
              hint="150-160 characters"
              pageId={pageId}
              editor={editor}
              openLocalization={openLocalization}
            >
              <textarea
                className="w-full px-3 py-2 bg-admin_canvas border border-admin_bordered rounded-lg text-admin_text resize-none focus:outline-none focus:ring-2 focus:ring-admin_primary"
                rows={3}
                value={seoData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Enter meta description..."
              />
            </FieldRow>

            <FieldRow
              label="Keywords"
              fieldName="seo_keywords"
              hint="Comma separated"
              pageId={pageId}
              editor={editor}
              openLocalization={openLocalization}
            >
              <Input
                variant="admin"
                value={seoData.keywords}
                onChange={(e) => handleChange('keywords', e.target.value)}
                placeholder="keyword1, keyword2, keyword3..."
              />
            </FieldRow>

            <FieldRow
              label="Social Image"
              fieldName="seo_og_image"
              pageId={pageId}
              editor={editor}
              openLocalization={openLocalization}
              hint="1200x630px recommended"
            >
              {seoData.ogImage ? (
                <div className="flex items-center gap-3">
                  <div className="relative group">
                    <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-admin_bordered bg-admin_canvas">
                      <img
                        src={url(seoData.ogImage)}
                        alt="OG Image"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button
                          onClick={handleRemoveImage}
                          className="p-2 bg-admin_error rounded-full text-white hover:bg-admin_error/80 transition-colors"
                          type="button"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="admin_secondary"
                    onClick={() => setIsMediaLibraryOpen(true)}
                  >
                    <PhotoIcon className="w-4 h-4 mr-1" />
                    Change
                  </Button>
                </div>
              ) : (
                <div
                  onClick={() => setIsMediaLibraryOpen(true)}
                  className="w-24 h-24 rounded-lg border-2 border-dashed border-admin_bordered bg-admin_canvas hover:border-admin_primary hover:bg-admin_neutral_1/20 transition-colors flex items-center justify-center cursor-pointer group"
                >
                  <PhotoIcon className="w-6 h-6 text-admin_text_secondary group-hover:text-admin_primary transition-colors" />
                </div>
              )}
            </FieldRow>
          </div>
        </div>
      </div>

      {/* Media Library */}
      {isMediaLibraryOpen && (
        <MediaLibrary
          isOpen={isMediaLibraryOpen}
          onClose={() => setIsMediaLibraryOpen(false)}
          onFileSelect={handleFileSelect}
          selectMode="single"
          fileType="image"
        />
      )}

      {/* Localization Editor */}
      {localizationEditor.isOpen && (
        <LocalizationItemEditor
          parentObject={editor.page}
          isOpen={localizationEditor.isOpen}
          localizationKey={localizationEditor.key}
          onClose={() => setLocalizationEditor({ isOpen: false, field: null, key: null })}
          defaultValue={seoData[localizationEditor.field === 'seo_title' ? 'title' :
                                   localizationEditor.field === 'seo_description' ? 'description' :
                                   localizationEditor.field === 'seo_keywords' ? 'keywords' : 'ogImage']}
          onSave={async (item) => {
            // Update the field value with the new English translation
            const newValue = item.translations.en.content
            const fieldMap = {
              'seo_title': 'title',
              'seo_description': 'description',
              'seo_keywords': 'keywords',
              'seo_og_image': 'ogImage'
            }
            const localField = fieldMap[localizationEditor.field]

            // Update local state
            setSeoData(prev => ({ ...prev, [localField]: newValue }))

            // Update page content directly
            editor.updateContent({
              [localizationEditor.field]: newValue
            })
          }}
        />
      )}
    </>
  )
}

export default SEOPlugin
